import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { signInWithPopup, signOut as firebaseSignOut } from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import api from '../services/api';
import { Member, AuthResponse } from '../types';

interface AuthContextType {
  user: Member | null;
  loading: boolean;
  authOrgRole: string | null;
  signInWithGoogle: () => Promise<string>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUserData: (user: Member) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Cache duration for user data - 1 hour (data rarely changes, mutations force refresh)
const USER_CACHE_TTL_MS = 60 * 60 * 1000;

const AUTH_ORG_ROLE_KEY = 'authOrgRole';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [authOrgRole, setAuthOrgRole] = useState<string | null>(
    () => localStorage.getItem(AUTH_ORG_ROLE_KEY)
  );

  // Keeps the cached role hint in step with the authoritative role the backend reports on
  // /members/me, so it can only ever be one request stale instead of one login stale.
  const syncOrgRoleHint = useCallback((member: Member) => {
    if (!member.securityRole) {
      return;
    }
    localStorage.setItem(AUTH_ORG_ROLE_KEY, member.securityRole);
    setAuthOrgRole(member.securityRole);
  }, []);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const lastFetch = localStorage.getItem('userLastFetch');

    if (token && storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
      try {
        const user = JSON.parse(storedUser);
        const now = Date.now();
        
        // Only fetch fresh data if cache is stale (older than 1 hour)
        if (!lastFetch || (now - parseInt(lastFetch)) > USER_CACHE_TTL_MS) {
          // Fetch fresh user data from backend
          api.get<Member>('/members/me')
            .then(response => {
              const updatedUser = response.data;
              localStorage.setItem('user', JSON.stringify(updatedUser));
              localStorage.setItem('userLastFetch', now.toString());
              setUser(updatedUser);
              syncOrgRoleHint(updatedUser);
            })
            .catch(error => {
              console.error('Failed to refresh user data:', error);
              // Fallback to cached data if refresh fails
              setUser(user);
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          // Use cached data (less than 1 hour old)
          setUser(user);
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        // Clear invalid data
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('userLastFetch');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [syncOrgRoleHint]);

  const signInWithGoogle = useCallback(async (): Promise<string> => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      
      // Get the ID token
      const idToken = await firebaseUser.getIdToken();

      // Send the token to your backend
      const response = await api.post<AuthResponse>('/auth/firebase', { token: idToken });

      // Store the JWT token and user data
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      const loginRole = response.data.user.organizationRole ?? null;
      localStorage.setItem(AUTH_ORG_ROLE_KEY, loginRole ?? '');
      setAuthOrgRole(loginRole);

      setUser(response.data.user);

      return idToken;
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      
      // Sign out from Firebase if backend authentication fails
      await firebaseSignOut(auth);
      
      // Extract error message from API response
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem(AUTH_ORG_ROLE_KEY);
      setUser(null);
      setAuthOrgRole(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get<Member>('/members/me');
      const updatedUser = response.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      localStorage.setItem('userLastFetch', Date.now().toString());
      setUser(updatedUser);
      syncOrgRoleHint(updatedUser);
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  }, [syncOrgRoleHint]);

  const setUserData = useCallback((userData: Member) => {
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('userLastFetch', Date.now().toString());
    setUser(userData);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, authOrgRole, signInWithGoogle, signOut, refreshUser, setUserData }}>
      {children}
    </AuthContext.Provider>
  );
};
